/**
 * Preload Fields Module
 * Precarga campos de formularios con valores UTM de la URL
 */

(function() {
    'use strict';

    /**
     * Obtiene todos los parámetros de la URL
     * @returns {Object} Objeto con todos los parámetros de la query string
     */
    function getUrlParams() {
        const params = {};
        const urlSearchParams = new URLSearchParams(window.location.search);
        
        for (const [key, value] of urlSearchParams) {
            params[key.toLowerCase()] = value;
        }
        
        return params;
    }

    /**
     * Verifica si un campo debe ser prellenado con datos UTM
     * @param {string} fieldName - Nombre del campo
     * @returns {boolean}
     */
    function isUtmField(fieldName) {
        const normalizedName = fieldName.toLowerCase();
        return normalizedName.startsWith('utm_') || 
               normalizedName === 'desde_url' || 
               normalizedName === 'utm_source';
    }

    /**
     * Obtiene el valor correspondiente para un campo UTM
     * @param {string} fieldName - Nombre del campo
     * @param {Object} urlParams - Parámetros de la URL
     * @returns {string|null} Valor del parámetro o null si no existe
     */
    function getUtmValue(fieldName, urlParams) {
        const normalizedName = fieldName.toLowerCase();
        
        // Mapeo especial para campos específicos
        if (normalizedName === 'desde_url') {
            return window.location.href;
        }
        
        // Para campos utm_*, buscar directamente en los parámetros
        return urlParams[normalizedName] || null;
    }

    /**
     * Prellenar un campo específico
     * @param {HTMLElement} field - Elemento del campo
     * @param {string} value - Valor a establecer
     */
    function preloadField(field, value) {
        if (!field || !value) return;
        
        const tagName = field.tagName.toLowerCase();
        const fieldType = field.type ? field.type.toLowerCase() : '';
        
        try {
            switch (tagName) {
                case 'input':
                    if (['text', 'email', 'url', 'tel', 'hidden'].includes(fieldType)) {
                        field.value = value;
                        // Disparar evento para notificar el cambio
                        field.dispatchEvent(new Event('input', { bubbles: true }));
                        field.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    break;
                    
                case 'textarea':
                    field.value = value;
                    field.dispatchEvent(new Event('input', { bubbles: true }));
                    field.dispatchEvent(new Event('change', { bubbles: true }));
                    break;
                    
                case 'select':
                    // Buscar opción que coincida con el valor
                    const option = Array.from(field.options).find(opt => 
                        opt.value.toLowerCase() === value.toLowerCase() ||
                        opt.text.toLowerCase() === value.toLowerCase()
                    );
                    if (option) {
                        field.value = option.value;
                        field.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    break;
            }
            
            // Agregar clase para indicar que el campo fue prellenado
            field.classList.add('utm-preloaded');
            
        } catch (error) {
            console.warn('Error prellenando campo:', field.name || field.id, error);
        }
    }

    /**
     * Busca y prellenar campos UTM en un formulario
     * @param {HTMLFormElement} form - Formulario a procesar
     * @param {Object} urlParams - Parámetros de la URL
     */
    function preloadFormFields(form, urlParams, currentUrl) {
        // Buscar todos los campos de entrada en el formulario
        const fields = form.querySelectorAll('input, textarea, select');
        
        fields.forEach(field => {
            const fieldName = field.name || field.id;
            
            if (fieldName == 'source_title') {
                field.value = document.title;
                return;
            }

            if (!fieldName || !isUtmField(fieldName)) {
                return;
            }
            
            // No sobrescribir campos que ya tienen valor (a menos que estén vacíos)
            if (field.value && field.value.trim() !== '') {
                return;
            }

            if (fieldName == 'desde_url') {
                if (currentUrl.includes('?')) {
                    field.value = currentUrl.split('?')[0];
                } else {
                    field.value = currentUrl;
                }
                console.log(`Campo desde_url prellenado: ${fieldName} = ${field.value}`);
            } else {
                const utmValue = getUtmValue(fieldName, urlParams);
                if (utmValue) {
                    preloadField(field, utmValue);
                    console.log(`Campo UTM prellenado: ${fieldName} = ${utmValue}`);
                }
            }
        });
    }

    /**
     * Busca formularios del plugin Gesvalt y los procesa
     */
    function processGesvaltForms() {
        const currentUrl = window.location.href;
        const urlParams = getUrlParams();
        
        
        // Buscar formularios del plugin (pueden tener diferentes selectores)
        const formSelectors = [
            'form.gesvalt-form'
        ];
        
        let formsFound = 0;
        
        formSelectors.forEach(selector => {
            const forms = document.querySelectorAll(selector);
            forms.forEach(form => {
                preloadFormFields(form, urlParams, currentUrl);
                formsFound++;
            });
        });
        
        
        if (formsFound > 0) {
            console.log(`Procesados ${formsFound} formularios para prellenado UTM`);
        }
    }

    /**
     * Observer para detectar formularios cargados dinámicamente
     */
    function setupDynamicFormObserver() {
        const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                mutation.addedNodes.forEach(function(node) {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Si se añadió un formulario
                        if (node.tagName === 'FORM') {
                            setTimeout(() => processGesvaltForms(), 100);
                        }
                        // Si se añadió un contenedor que puede contener formularios
                        else if (node.querySelector && node.querySelector('form')) {
                            setTimeout(() => processGesvaltForms(), 100);
                        }
                    }
                });
            });
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    /**
     * Inicialización del módulo
     */
    function init() {
        // Procesar formularios existentes cuando el DOM esté listo
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', processGesvaltForms);
        } else {
            processGesvaltForms();
        }
        
        // Configurar observer para formularios dinámicos
        setupDynamicFormObserver();
        
        // También procesar después de que se cargue completamente la página
        window.addEventListener('load', function() {
            setTimeout(processGesvaltForms, 500);
        });
    }

    // Inicializar el módulo
    init();

    // Exponer función pública para uso manual si es necesario
    window.GesvaltPreloadFields = {
        process: processGesvaltForms,
        getUrlParams: getUrlParams
    };

})();